<script setup lang="ts">
import { ref } from 'vue'
import type { ContactChannels, LocalizedCopy, SiteConfig } from '../types/site'
import { useSectionReveal } from '../composables/useSectionReveal'
import IconSprite from './IconSprite.vue'
import SectionTitle from './SectionTitle.vue'

defineProps<{
  copy: LocalizedCopy
  channels: ContactChannels
  icons: SiteConfig['media']['contactIcons']
  sprites?: SiteConfig['media']['contactSprites']
  watermarkImage: string
}>()

const sectionRoot = ref<HTMLElement | null>(null)
const { active } = useSectionReveal(sectionRoot)

function emailHref(email: string, subject: string): string {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}`
}
</script>

<template>
  <section id="contact" ref="sectionRoot" class="contact-section page-section">
    <SectionTitle
      eyebrow="CONTACT"
      :title="copy.contact.title"
      :watermark="watermarkImage"
    />
    <p class="contact-section__intro">{{ copy.contact.intro }}</p>

    <div class="contact-grid">
      <article class="contact-card contact-card--ip">
        <div class="contact-card__header">
          <div>
            <h3>{{ copy.contact.ip.title }}</h3>
          </div>
          <span class="icon-motion contact-card__art-wrap contact-card__art-wrap--ip">
            <IconSprite v-if="sprites?.ip" :sprite="sprites.ip!" :active="active" />
            <img
              v-else
              class="icon-motion__image"
              :src="icons.ip"
              alt=""
              loading="lazy"
              decoding="async"
            />
          </span>
        </div>
        <p>{{ copy.contact.ip.description }}</p>
        <a class="contact-link" data-track="contact-ip-email" :href="emailHref(channels.ip, copy.contact.ip.subject)">
          <span>
            <small>{{ copy.contact.emailAction }}</small>
            <strong>{{ channels.ip }}</strong>
          </span>
          <span aria-hidden="true">↗</span>
        </a>
      </article>

      <article class="contact-card contact-card--business">
        <div class="contact-card__header">
          <div>
            <h3>{{ copy.contact.business.title }}</h3>
          </div>
          <span class="icon-motion contact-card__art-wrap contact-card__art-wrap--business">
            <IconSprite v-if="sprites?.business" :sprite="sprites.business!" :active="active" />
            <img
              v-else
              class="icon-motion__image"
              :src="icons.business"
              alt=""
              loading="lazy"
              decoding="async"
            />
          </span>
        </div>
        <p>{{ copy.contact.business.description }}</p>
        <div class="contact-card__links">
          <a class="contact-link" data-track="contact-media-email" :href="emailHref(channels.media, copy.contact.business.mediaSubject)">
            <span>
              <small>{{ copy.contact.business.mediaLabel }}</small>
              <strong>{{ channels.media }}</strong>
            </span>
            <span aria-hidden="true">↗</span>
          </a>
          <a class="contact-link" data-track="contact-investor-email" :href="emailHref(channels.investors, copy.contact.business.investorSubject)">
            <span>
              <small>{{ copy.contact.business.investorLabel }}</small>
              <strong>{{ channels.investors }}</strong>
            </span>
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </article>

      <article class="contact-card contact-card--feedback">
        <div class="contact-card__header">
          <div>
            <h3>{{ copy.contact.feedback.title }}</h3>
          </div>
          <span class="icon-motion contact-card__art-wrap contact-card__art-wrap--feedback">
            <IconSprite v-if="sprites?.feedback" :sprite="sprites.feedback!" :active="active" />
            <img
              v-else
              class="icon-motion__image"
              :src="icons.feedback"
              alt=""
              loading="lazy"
              decoding="async"
            />
          </span>
        </div>
        <p>{{ copy.contact.feedback.description }}</p>
        <a class="contact-link" data-track="contact-feedback-email" :href="emailHref(channels.feedback, copy.contact.feedback.subject)">
          <span>
            <small>{{ copy.contact.emailAction }}</small>
            <strong>{{ channels.feedback }}</strong>
          </span>
          <span aria-hidden="true">↗</span>
        </a>
        <a class="contact-service" data-track="contact-service" :href="channels.customerService">
          {{ copy.contact.serviceAction }}
          <span aria-hidden="true">→</span>
        </a>
      </article>
    </div>
  </section>
</template>
